import { useMemo, useState, type FC } from 'react';
import { Link } from 'react-router-dom';
import {
    LuCheck, LuClock, LuTriangleAlert, LuCircleHelp, LuUsers,
} from 'react-icons/lu';
import type { PerformanceIndividual, ProjecaoFechamento } from '../services/RelatorioService';
import { emAndamento, gruposDoFunil } from '../data/etapas';
import type { Opportunity } from '../services/OpportunityService';
import type { Equipe } from '../services/EquipeService';
import './IndividualTab.css';

export type GoalsTimeframe = 'month' | 'week' | 'day';

interface WorkingDaysInfo {
    monthlyDays: number;
    weeklyDays: number;
    elapsedDays: number;
}

interface IndividualTabProps {
    agent: PerformanceIndividual | null;
    projecao: ProjecaoFechamento | null;
    monitoringMonth: string;
    opportunities: Opportunity[];
    individualPerf: PerformanceIndividual[];
    teams: Equipe[];
    workingDaysInfo: WorkingDaysInfo;
    timeframe: GoalsTimeframe;
    onTimeframeChange: (tf: GoalsTimeframe) => void;
}

const TIMEFRAMES: { id: GoalsTimeframe; label: string; noun: string }[] = [
    { id: 'month', label: 'Mês', noun: 'no mês' },
    { id: 'week', label: 'Semana', noun: 'na semana' },
    { id: 'day', label: 'Dia', noun: 'no dia' },
];

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatCurrencyShort = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value || 0);

const formatDecimal = (value: number) =>
    value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const initialsOf = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length > 1) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
};

/**
 * Aba Individual — o dossiê de uma pessoa. Traz para a tela tudo que antes
 * vivia no `SellerMonitoringModal`; os cálculos são os mesmos, para os números
 * não divergirem enquanto as duas telas coexistem.
 */
const IndividualTab: FC<IndividualTabProps> = ({
    agent,
    projecao,
    monitoringMonth,
    opportunities,
    individualPerf,
    teams,
    workingDaysInfo,
    timeframe,
    onTimeframeChange,
}) => {
    const [showHelp, setShowHelp] = useState(false);

    const userTeam = useMemo(() => {
        if (!agent) return undefined;
        return teams.find(t =>
            t.membroIds?.includes(agent.usuarioId) ||
            t.membros?.some(m => m.id === agent.usuarioId)
        );
    }, [teams, agent]);

    const assignedDeals = useMemo(() => {
        if (!agent) return [];
        return opportunities.filter(op => op.nomeUsuarioResponsavel === agent.usuarioNome);
    }, [opportunities, agent]);

    const closedSalesInMonth = useMemo(() =>
        assignedDeals
            .filter(op => op.idStauts === 3 && op.dataFechamentoVenda?.startsWith(monitoringMonth))
            .sort((a, b) =>
                new Date(b.dataFechamentoVenda!).getTime() - new Date(a.dataFechamentoVenda!).getTime()),
        [assignedDeals, monitoringMonth]);

    const closedSalesTotal = useMemo(
        () => closedSalesInMonth.reduce((s, op) => s + (op.valor || 0), 0), [closedSalesInMonth]);

    const totalLeads = assignedDeals.length;

    /* Em andamento: miolo menos a entrada — era só a etapa 4, que some num funil desenhado pela empresa. */
    const proposalsPending = useMemo(
        () => assignedDeals.filter(op => emAndamento(op.idStauts)).length, [assignedDeals]);

    const lastActivity = useMemo(() => {
        let date: Date | null = null;
        let detail = '';
        let dealId: number | null = null;
        assignedDeals.forEach(op => {
            op.detalhesConversa?.forEach(det => {
                const d = new Date(det.dataDetalheCriado);
                if (!date || d > date) {
                    date = d;
                    detail = det.descricaoDetalhe;
                    dealId = op.idConversa;
                }
            });
        });
        return { date: date as Date | null, detail, dealId };
    }, [assignedDeals]);

    const daysSinceLastActivity = useMemo(() => {
        if (!lastActivity.date) return null;
        return Math.floor(Math.abs(Date.now() - lastActivity.date.getTime()) / (1000 * 60 * 60 * 24));
    }, [lastActivity.date]);

    const dailyCalls = agent ? agent.totalLigacoes / (workingDaysInfo.elapsedDays || 1) : 0;

    const teamAverageDailyCalls = useMemo(() => {
        if (individualPerf.length === 0) return 0;
        const total = individualPerf.reduce((s, p) => s + p.totalLigacoes, 0);
        return (total / individualPerf.length) / (workingDaysInfo.elapsedDays || 1);
    }, [individualPerf, workingDaysInfo.elapsedDays]);

    /* As faixas são as colunas do funil da empresa, menos a do robô: etapa que ela criar entra sozinha. */
    const funnelStages = useMemo(() => {
        const stages = gruposDoFunil()
            .filter(g => g.cor !== 'ia')
            .map(g => ({ statusIds: g.statusIds, name: g.title, count: 0, value: 0 }));
        assignedDeals.forEach(op => {
            const stage = stages.find(s => s.statusIds.includes(op.idStauts));
            if (stage) {
                stage.count++;
                stage.value += op.valor || 0;
            }
        });
        return stages.map(({ name, count, value }) => ({ name, count, value }));
    }, [assignedDeals]);

    const funnelMax = useMemo(
        () => Math.max(...funnelStages.map(s => s.value), 1), [funnelStages]);

    const conversionRate = useMemo(() => {
        const won = assignedDeals.filter(op => op.idStauts === 3).length;
        return totalLeads > 0 ? (won / totalLeads) * 100 : 0;
    }, [assignedDeals, totalLeads]);

    const globalConversionRate = useMemo(() => {
        const won = opportunities.filter(op => op.idStauts === 3).length;
        return opportunities.length > 0 ? (won / opportunities.length) * 100 : 0;
    }, [opportunities]);

    const stagnantDealsCount = useMemo(() => {
        const now = Date.now();
        return assignedDeals.filter(op => {
            if (op.idStauts === 3 || op.idStauts === 6) return false;
            let last = new Date(op.dataConversaCriada).getTime();
            op.detalhesConversa?.forEach(det => {
                const d = new Date(det.dataDetalheCriado).getTime();
                if (d > last) last = d;
            });
            return (now - last) / (1000 * 60 * 60 * 24) > 5;
        }).length;
    }, [assignedDeals]);

    const projectedClosure = useMemo(() => {
        if (projecao) return projecao.projecaoFechamento;
        if (!agent || workingDaysInfo.elapsedDays <= 0) return 0;
        return (agent.totalFaturado / workingDaysInfo.elapsedDays) * workingDaysInfo.monthlyDays;
    }, [projecao, agent, workingDaysInfo]);

    const remainingWorkingDays = useMemo(() => {
        if (projecao) return Math.max(projecao.diasUteisTotais - projecao.diasUteisDecorridos, 0);
        return Math.max(workingDaysInfo.monthlyDays - workingDaysInfo.elapsedDays, 0);
    }, [projecao, workingDaysInfo]);

    const totalWorkingDays = projecao?.diasUteisTotais ?? workingDaysInfo.monthlyDays;

    /* O seletor Mês/Semana/Dia reescala meta e realizado na mesma proporção
       usada hoje no bloco coletivo — é uma leitura de ritmo, não outro dado. */
    const scoped = useMemo(() => {
        const monthlyGoal = agent?.metaFaturamento ?? 0;
        const monthlyRealized = agent?.totalFaturado ?? 0;
        const perDayGoal = monthlyGoal / (workingDaysInfo.monthlyDays || 1);
        const perDayRealized = monthlyRealized / (workingDaysInfo.elapsedDays || 1);

        if (timeframe === 'day') return { goal: perDayGoal, realized: perDayRealized };
        if (timeframe === 'week') {
            return {
                goal: perDayGoal * workingDaysInfo.weeklyDays,
                realized: perDayRealized * workingDaysInfo.weeklyDays,
            };
        }
        return { goal: monthlyGoal, realized: monthlyRealized };
    }, [agent, timeframe, workingDaysInfo]);

    const requiredDaily = (agent?.metaFaturamento ?? 0) / (totalWorkingDays || 1);

    /* Mesma régua do Analytics: a meta é um marcador, não o fim da barra. */
    const bullet = useMemo(() => {
        const scale = Math.max(scoped.realized, scoped.goal) * 1.1 || 1;
        const pctOf = (v: number) => Math.max(0, Math.min(100, (v / scale) * 100));
        const achievement = scoped.goal > 0 ? (scoped.realized / scoped.goal) * 100 : 0;
        return {
            fill: pctOf(scoped.realized),
            nearZero: pctOf(scoped.realized) < 6,
            metaAt: pctOf(scoped.goal),
            achievement,
            beat: scoped.goal > 0 && scoped.realized >= scoped.goal,
            onTrack: scoped.goal > 0 && achievement >= 70 && achievement < 100,
            behind: scoped.goal > 0 && achievement < 70,
        };
    }, [scoped]);

    const timeframeNoun = TIMEFRAMES.find(t => t.id === timeframe)?.noun ?? 'no mês';

    if (!agent) {
        return (
            <div className="it-empty chart-card">
                <LuUsers className="it-empty-icon" />
                <h4>Nenhum vendedor para este mês</h4>
                <p>Sem dados de performance individual no período selecionado.</p>
            </div>
        );
    }

    return (
        <div className="individual-tab">

            {/* ═══ 1. META DA PESSOA ═══════════════════════════════ */}
            <section className="chart-card it-hero">
                <div className="it-hero-id">
                    <div className="it-avatar">{initialsOf(agent.usuarioNome)}</div>
                    <div className="it-id-text">
                        <span className="it-name">{agent.usuarioNome}</span>
                        <span className="it-team">{userTeam ? `Equipe: ${userTeam.nome}` : 'Sem equipe'}</span>
                    </div>

                    {bullet.beat && <span className="an-chip an-chip-good"><LuCheck /> Meta atingida</span>}
                    {bullet.onTrack && <span className="an-chip an-chip-info"><LuClock /> No caminho</span>}
                    {bullet.behind && <span className="an-chip an-chip-warn"><LuTriangleAlert /> Em risco</span>}

                    <div className="it-hero-controls">
                        <div className="it-timeframe">
                            {TIMEFRAMES.map(tf => (
                                <button
                                    key={tf.id}
                                    type="button"
                                    className={`it-timeframe-btn ${timeframe === tf.id ? 'is-active' : ''}`}
                                    onClick={() => onTimeframeChange(tf.id)}
                                >
                                    {tf.label}
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            className="it-help-btn"
                            onClick={() => setShowHelp(v => !v)}
                            title="Como esses números são calculados?"
                            aria-label="Como esses números são calculados?"
                        >
                            <LuCircleHelp />
                        </button>
                    </div>
                </div>

                {showHelp && (
                    <div className="it-help">
                        <strong>Como calculamos a projeção</strong>
                        <p>
                            Pegamos o quanto {agent.usuarioNome.split(' ')[0]} faturou até hoje e dividimos pelos
                            dias úteis já decorridos, achando a média diária. Depois multiplicamos por todos os
                            dias úteis do mês.
                        </p>
                        {projecao && (
                            <p className="it-help-note">
                                Projeção calculada com base na data real de fechamento das vendas
                                ({projecao.diasUteisDecorridos} de {projecao.diasUteisTotais} dias úteis decorridos).
                            </p>
                        )}
                    </div>
                )}

                <div className="it-hero-figures">
                    <div className="it-hero-value-block">
                        <div className="it-hero-value-row">
                            <strong className="it-hero-value">{formatCurrencyShort(scoped.realized)}</strong>
                            {scoped.goal > 0 && (
                                <span className={`it-hero-pct ${bullet.beat ? 'is-good' : bullet.behind ? 'is-warn' : ''}`}>
                                    {Math.round(bullet.achievement)}%
                                </span>
                            )}
                        </div>
                        <span className="it-hero-sub">
                            {scoped.goal > 0
                                ? `de ${formatCurrencyShort(scoped.goal)} de meta ${timeframeNoun}`
                                : 'sem meta definida para este vendedor'}
                        </span>
                    </div>

                    <div className="it-stats">
                        <div className="it-stat">
                            <span className="it-stat-label">Ritmo atual</span>
                            <strong className="it-stat-value">
                                {formatCurrencyShort(agent.totalFaturado / (workingDaysInfo.elapsedDays || 1))}
                                <span className="it-stat-unit">/dia útil</span>
                            </strong>
                        </div>
                        <div className="it-stat">
                            <span className="it-stat-label">Necessário</span>
                            <strong className="it-stat-value is-muted">
                                {formatCurrencyShort(requiredDaily)}<span className="it-stat-unit">/dia útil</span>
                            </strong>
                        </div>
                        <div className="it-stat">
                            <span className="it-stat-label">Projeção</span>
                            <strong className="it-stat-value">{formatCurrencyShort(projectedClosure)}</strong>
                        </div>
                        <div className="it-stat">
                            <span className="it-stat-label">Dias úteis restantes</span>
                            <strong className="it-stat-value">
                                {remainingWorkingDays}<span className="it-stat-unit"> de {totalWorkingDays}</span>
                            </strong>
                        </div>
                    </div>
                </div>

                {scoped.goal > 0 && (
                    <div className="an-bullet">
                        <div className="an-bullet-track">
                            <div className="an-bullet-fill" style={{ width: `${bullet.fill}%` }} />
                            <div className="an-bullet-meta" style={{ left: `${bullet.metaAt}%` }} />
                        </div>
                        <div className="an-bullet-labels">
                            {/* realizado colado no zero: a tag dele ocupa o lugar do "R$ 0" */}
                            {!bullet.nearZero && <span className="an-bullet-zero">R$ 0</span>}
                            <span className="an-bullet-tag is-meta" style={{ left: `${bullet.metaAt}%` }}>
                                meta {formatCurrencyShort(scoped.goal)}
                            </span>
                            {Math.abs(bullet.fill - bullet.metaAt) > 14 && (
                                <span
                                    className={`an-bullet-tag is-value ${bullet.nearZero ? 'is-start' : ''}`}
                                    style={{ left: bullet.nearZero ? 0 : `${bullet.fill}%` }}
                                >
                                    {formatCurrencyShort(scoped.realized)}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {timeframe !== 'month' && (
                    <p className="it-note">Ritmo, projeção e dias restantes são sempre do mês.</p>
                )}
            </section>

            {/* ═══ 2. ATIVIDADE ════════════════════════════════════ */}
            <section className="chart-card">
                <header className="an-card-head">
                    <h3>Atividade no mês</h3>
                </header>

                <div className="it-activity">
                    <div className="it-metric">
                        <span className="it-metric-label">Ligações realizadas</span>
                        <div className="it-metric-value-row">
                            <strong className="it-metric-value">{agent.totalLigacoes}</strong>
                            <span className="it-metric-of">/ {agent.metaLigacoes}</span>
                        </div>
                        <div className="it-meter">
                            <div
                                className="it-meter-fill"
                                style={{ width: `${agent.metaLigacoes > 0 ? Math.min((agent.totalLigacoes / agent.metaLigacoes) * 100, 100) : 0}%` }}
                            />
                        </div>
                    </div>

                    {/* A meta de "Qtd. de vendas" era cadastrável e não aparecia em
                        tela nenhuma; entrou aqui em 2026-09-18 junto com a tela nova
                        de Metas. Sem meta definida, mostra só o realizado — barra
                        contra zero não diz nada. */}
                    <div className="it-metric">
                        <span className="it-metric-label">Vendas fechadas</span>
                        <div className="it-metric-value-row">
                            <strong className="it-metric-value">{agent.totalVendas ?? 0}</strong>
                            {agent.metaVendas > 0 && <span className="it-metric-of">/ {agent.metaVendas}</span>}
                        </div>
                        {agent.metaVendas > 0 && (
                            <div className="it-meter">
                                <div
                                    className="it-meter-fill"
                                    style={{ width: `${Math.min(((agent.totalVendas ?? 0) / agent.metaVendas) * 100, 100)}%` }}
                                />
                            </div>
                        )}
                    </div>

                    <div className="it-metric">
                        <span className="it-metric-label">Leads contatados</span>
                        <strong className="it-metric-value">{totalLeads}</strong>
                    </div>

                    <div className="it-metric">
                        <span className="it-metric-label">Em andamento</span>
                        <strong className="it-metric-value">{proposalsPending}</strong>
                    </div>

                    <div className="it-metric">
                        <span className="it-metric-label">Média diária de ligações</span>
                        <div className="it-metric-value-row">
                            <strong className="it-metric-value">{formatDecimal(dailyCalls)}</strong>
                            <span className={`it-metric-cmp ${dailyCalls >= teamAverageDailyCalls ? 'is-good' : 'is-warn'}`}>
                                equipe {formatDecimal(teamAverageDailyCalls)}
                            </span>
                        </div>
                        <div className="it-compare">
                            <div
                                className="it-compare-mine"
                                style={{ width: `${Math.min((dailyCalls / Math.max(dailyCalls, teamAverageDailyCalls, 0.1)) * 100, 100)}%` }}
                            />
                            <div
                                className="it-compare-team"
                                style={{ width: `${Math.min((teamAverageDailyCalls / Math.max(dailyCalls, teamAverageDailyCalls, 0.1)) * 100, 100)}%` }}
                            />
                        </div>
                    </div>
                </div>

                <div className="it-last">
                    <LuClock />
                    <div className="it-last-text">
                        <span className="it-last-label">Última atividade registrada</span>
                        {lastActivity.date ? (
                            <div className="it-last-content">
                                <span className={`it-last-when ${daysSinceLastActivity !== null && daysSinceLastActivity > 2 ? 'is-warn' : ''}`}>
                                    {daysSinceLastActivity === 0 ? 'Hoje'
                                        : daysSinceLastActivity === 1 ? 'Ontem'
                                            : `Há ${daysSinceLastActivity} dias`}
                                    {' '}({lastActivity.date.toLocaleDateString('pt-BR')})
                                </span>
                                <span className="it-last-desc">"{lastActivity.detail}"</span>
                                {lastActivity.dealId && (
                                    <Link to={`/pipeline/deal/${lastActivity.dealId}`} className="it-last-link">
                                        Ver oportunidade ›
                                    </Link>
                                )}
                            </div>
                        ) : (
                            <span className="it-last-none">Nenhuma atividade registrada neste mês</span>
                        )}
                    </div>
                </div>
            </section>

            {/* ═══ 3. FUNIL ════════════════════════════════════════ */}
            <section className="chart-card">
                <header className="an-card-head">
                    <h3>Funil de {agent.usuarioNome.split(' ')[0]}</h3>
                </header>

                <div className="it-funnel">
                    <div className="it-funnel-row it-funnel-head">
                        <span>Etapa do funil</span>
                        <span className="is-center">Quantidade</span>
                        <span>Volume</span>
                        <span className="is-right">Volume financeiro</span>
                    </div>
                    {funnelStages.map(stage => (
                        <div key={stage.name} className="it-funnel-row">
                            <span className={`it-funnel-name ${stage.count === 0 ? 'is-empty' : ''}`}>{stage.name}</span>
                            <span className={`is-center it-funnel-count ${stage.count === 0 ? 'is-empty' : ''}`}>{stage.count}</span>
                            <div className="an-bar-track">
                                <div className="an-bar-fill" style={{ width: `${(stage.value / funnelMax) * 100}%` }} />
                            </div>
                            <span className={`is-right it-funnel-value ${stage.value === 0 ? 'is-empty' : ''}`}>
                                {stage.value > 0 ? formatCurrencyShort(stage.value) : '—'}
                            </span>
                        </div>
                    ))}
                </div>

                <div className="it-funnel-foot">
                    <div className="it-conversion">
                        <div className="it-conversion-item">
                            <span className="it-metric-label">Conversão dela</span>
                            <strong className="it-conversion-mine">{formatDecimal(conversionRate)}%</strong>
                        </div>
                        <div className="it-conversion-divider" />
                        <div className="it-conversion-item">
                            <span className="it-metric-label">Média da empresa</span>
                            <strong className="it-conversion-global">{formatDecimal(globalConversionRate)}%</strong>
                        </div>
                    </div>

                    {stagnantDealsCount > 0 && (
                        <div className="it-alert">
                            <LuTriangleAlert />
                            <span>
                                <strong>{stagnantDealsCount}</strong>
                                {stagnantDealsCount === 1 ? ' negócio está' : ' negócios estão'} sem nenhuma
                                interação há mais de 5 dias.
                            </span>
                        </div>
                    )}
                </div>
            </section>

            {/* ═══ 4. NEGÓCIOS GANHOS ══════════════════════════════ */}
            <section className="chart-card">
                <header className="an-card-head">
                    <h3>Negócios ganhos no mês</h3>
                    <span className="an-card-meta">
                        {closedSalesInMonth.length} {closedSalesInMonth.length === 1 ? 'negócio' : 'negócios'} · {formatCurrency(closedSalesTotal)}
                    </span>
                </header>

                {closedSalesInMonth.length === 0 ? (
                    <p className="chart-empty">Nenhum negócio ganho neste mês.</p>
                ) : (
                    <div className="it-sales">
                        <div className="it-sales-row it-sales-head">
                            <span>Cliente</span>
                            <span>Data de fechamento</span>
                            <span className="is-right">Valor</span>
                        </div>
                        {closedSalesInMonth.map(op => (
                            <Link key={op.idConversa} to={`/pipeline/deal/${op.idConversa}`} className="it-sales-row">
                                <span className="it-sales-client">
                                    <span className="it-sales-name">{op.nomeContato || '—'}</span>
                                    <span className="it-sales-phone">{op.numeroWhatsapp}</span>
                                </span>
                                <span className="it-sales-date">
                                    {op.dataFechamentoVenda
                                        ? new Date(op.dataFechamentoVenda).toLocaleDateString('pt-BR')
                                        : '—'}
                                </span>
                                <span className="is-right it-sales-value">{formatCurrencyShort(op.valor || 0)}</span>
                            </Link>
                        ))}
                        <div className="it-sales-row it-sales-total">
                            <span>Total</span>
                            <span />
                            <span className="is-right">{formatCurrency(closedSalesTotal)}</span>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
};

export default IndividualTab;
